import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TopicHandlingError, WebPubSubResponse } from '@festo-ax/smartenance-shared-ui';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import * as SapAssetsAction from 'app/actions/sap-assets';
import * as fromProfile from 'app/reducers/profile';
import * as fromRouter from 'app/reducers/router';
import * as fromSapAssets from 'app/reducers/sap-assets';
import { SapAssetsService } from 'app/services/sap-assets.service';
import { AssetsUtils } from 'app/utils/reducers/assets.utils';
import { SelectedSapAsset } from 'app/models/reducers/sap-assets';
import { SapHierarchyPathResponse, SapHierarchyResponse } from 'app/models/api/sap-asset';
import { ApplicationStore } from '../state-management/application/application.store';

@Injectable()
export class SapAssetsEffects {
  private actions$: Actions = inject(Actions);
  private sapAssetsService: SapAssetsService = inject(SapAssetsService);
  private store: Store = inject(Store);
  private appStore: ApplicationStore = inject(ApplicationStore);

  initSapAssetTree$ = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.initSapAssetTree),
    map(() => SapAssetsAction.getSelectedSapNodeFromStorage()),
  ));

  getSelectedNodeFromStorageComplete$ = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.getSelectedSapNodeFromStorageComplete),
    concatLatestFrom(() => [
      this.store.select(fromRouter.selectedSapAsset),
    ]),
    map(([, selected]: [Action, SelectedSapAsset]) => {
      if (!selected || !selected.nodeId || selected.nodeId === 'root') {
        return SapAssetsAction.getInitialSapAssetsData();
      }

      return SapAssetsAction.getInitialSapAssetsDataForSelected({ selected });
    }),
  ));

  getInitialSapAssetsData$ = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.getInitialSapAssetsData),
    concatLatestFrom(() => [
      this.store.select(fromProfile.selectProfile),
      this.store.select(fromProfile.selectUserSettings)
    ]),
    switchMap(([, identity, userSettings]) =>
      this.sapAssetsService.getGroupHierarchy(null, identity, userSettings).pipe(
        map((response: WebPubSubResponse<'getFunctionalLocationAssetHierarchy'>) => {
          if (!response.data?.result?.items || !!response.data?.topicHandlingError) {
            const error: string = this.getErrorMessages(response.data?.topicHandlingError);

            return SapAssetsAction.getInitialSapAssetsDataFailed({ error });
          }
          return SapAssetsAction.getInitialSapAssetsDataSuccess({ response });
        }),
        catchError(error => of(SapAssetsAction.getInitialSapAssetsDataFailed({ error })))
      ))
  ));

  calculateExpandCollapseSapNodesCases$ = createEffect(() => this.actions$.pipe(
    ofType(
      SapAssetsAction.getInitialSapAssetsDataSuccess,
      SapAssetsAction.expandCollapseSapNode,
      SapAssetsAction.loadSapAssetTreePartSuccess,
      SapAssetsAction.loadSapAssetTreePartFailed,
      SapAssetsAction.openTargetElementOnTree,
      SapAssetsAction.changeSapActive
    ),
    concatLatestFrom(() => [
      this.store.select(fromSapAssets.selectShowIsActive)
    ]),
    map(([, isActive]) => SapAssetsAction.calculateExpandCollapseSapNodes({ isActive })),
  ));

  getInitialSapAssetsDataForSelected$ = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.getInitialSapAssetsDataForSelected),
    concatLatestFrom(() => [
      this.store.select(fromProfile.selectProfile),
      this.store.select(fromProfile.selectUserSettings)
    ]),
    switchMap(([{ selected: { nodeId, nodeType } }, identity, userSettings]) =>
      this.sapAssetsService.getSapAssetTreePathByNodeId(nodeId, nodeType, identity, userSettings).pipe(
        map((response: SapHierarchyPathResponse) => {
          if (!response.data?.result?.item|| !!response.data?.topicHandlingError) {
            const error: string = this.getErrorMessages(response.data?.topicHandlingError);

            return SapAssetsAction.getInitialSapAssetsDataForSelectedFailed({ error });
          }
          return SapAssetsAction.getInitialSapAssetsDataForSelectedSuccess({ response, nodeId });
        }),
        catchError(error => of(SapAssetsAction.getInitialSapAssetsDataForSelectedFailed({ error })))
      ))
  ));

  getInitialSapAssetsDataForSelectedSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.getInitialSapAssetsDataForSelectedSuccess),
    map(({ nodeId }) => SapAssetsAction.openTargetElementOnTree({ nodeId })),
  ));

  loadSapAssetTreePart$ = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.loadSapAssetTreePart),
    concatLatestFrom(() => [
      this.store.select(fromProfile.selectProfile),
      this.store.select(fromProfile.selectUserSettings)
    ]),
    concatMap(([{ nodeId, nodeType }, identity, userSettings]) =>
      this.sapAssetsService.getSapAssetTreePartByNodeId(nodeId, nodeType, identity, userSettings).pipe(
        map((response: SapHierarchyResponse) => {
          if (!response.data?.result?.items || !!response.data?.topicHandlingError) {
            const error: string = this.getErrorMessages(response.data?.topicHandlingError);

            return SapAssetsAction.loadSapAssetTreePartFailed({ error, nodeId });
          }
          return SapAssetsAction.loadSapAssetTreePartSuccess({ response, nodeId });
        }),
        catchError(error => of(SapAssetsAction.loadSapAssetTreePartFailed({ error, nodeId })))
      ),
    )
  ));

  openSearchedElementOnTree$ = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.openSearchedElementOnTree),
    concatLatestFrom(() => [
      this.store.select(fromSapAssets.selectSapAssetTreeView),
    ]),
    map(([{ nodeId, nodeType }, tree]) =>
      AssetsUtils.isTargetElementOnTree(tree, nodeId)
        ? SapAssetsAction.openTargetElementOnTree({ nodeId })
        : SapAssetsAction.getInitialSapAssetsDataForSelected({ selected: { nodeId, nodeType } })
    ),
  ));

  reloadFailedSapNode$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.reloadFailedSapNode),
    map(({ nodeId, nodeType }) => SapAssetsAction.loadSapAssetTreePart({ nodeId, nodeType })),
  ));

  expandCollapseSapNode$ = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.expandCollapseSapNode),
    map(({ nodeId, nodeType }) => SapAssetsAction.checkForLoadedSapChildren({ nodeId, nodeType })),
  ));

  checkForLoadedSapChildren$ = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.checkForLoadedSapChildren),
    map(({ nodeId, nodeType }) => ({ nodeId, nodeType })),
    concatLatestFrom(() => [
      this.store.select(fromSapAssets.selectSapAssetTreeView),
      this.store.select(fromSapAssets.selectNodesInLoading),
    ]),
    switchMap(([{ nodeId, nodeType }, tree, inLoading]) =>
      AssetsUtils.isChildrenOfNodeAreNotLoaded(tree, nodeId, inLoading)
        ? of(SapAssetsAction.loadSapAssetTreePart({ nodeId, nodeType }))
        : EMPTY),
  ));

  storeSelectedSapNode$ = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.storeSelectedSapNode),
    map(({ selected }) => SapAssetsAction.storeSelectedSapNodeInStorage({ selected })),
  ));

  storeSelectedSapNodeInStorage$ = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.storeSelectedSapNodeInStorage),
    tap((action) => this.appStore.setSapAssetsData(action.selected)),
  ), { dispatch: false });

  getSelectedSapNodeFromStorage$ = createEffect(() => this.actions$.pipe(
    ofType(SapAssetsAction.getSelectedSapNodeFromStorage),
    switchMap(() => of(this.appStore.sapAssetsData()).pipe(
      map((selected: SelectedSapAsset | null) => SapAssetsAction.getSelectedSapNodeFromStorageComplete({ selected })),
    ))
  ));

  getErrorMessages(topicHandler: TopicHandlingError | null): string | null {
    if (!topicHandler) {
      return null;
    }

    let error: string = '';

    if (topicHandler.StatusCode) {
      error += `${topicHandler.StatusCode}`
    }
    if (topicHandler.StatusCode && topicHandler.Message) {
      error += ': ';
    }
    if (topicHandler.Message) {
      error += topicHandler.Message;
    }

    return error;
  }
}
